import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../components/Services.css';
import '../components/About.css';

const ResearchTeamPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const teamInfo = {
    title: 'Research & Awareness',
    description: 'Shining a light on untold stories. We dive deep into the realities of aging, tackling issues like isolation, ageism, accessibility, and intergenerational divides. Our team runs a monthly newsletter featuring interviews, insights, and stories that highlight the experiences of older adults, turning knowledge into action. We also collaborate with the Engineering team to ensure that research findings help shape better, more accessible games.',
    video: '/videos/6646679-hd_1080_1920_24fps.mp4',
    keyPoints: [
      'Conduct research on aging, isolation, and ageism',
      'Publish monthly newsletter with interviews and insights',
      'Highlight experiences and stories of older adults',
      'Collaborate with Engineering team on accessibility research',
      'Turn research findings into actionable solutions',
      'Raise awareness about intergenerational divides'
    ],
    skills: [
      'Research Methods',
      'Interviewing',
      'Data Analysis',
      'Writing & Editing',
      'Newsletter Production',
      'Advocacy'
    ]
  };

  return (
    <div className="page-container">
      <section className="services section" ref={sectionRef}>
        <div className="container">
          <div className={`services-header ${isVisible ? 'animate-fade-in' : ''}`}>
            <h1 className="section-title">{teamInfo.title}</h1>
            <p className="section-subtitle">
              {teamInfo.description}
            </p>
          </div>

          <div className={`team-hero-section ${isVisible ? 'animate-slide-up' : ''}`}>
            <div className="team-hero-video">
              <video 
                src={teamInfo.video} 
                className="team-bg-video"
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
          </div>

          <div className="team-details-grid">
            <div className={`team-details-card ${isVisible ? 'animate-slide-left' : ''}`}>
              <h2>What We Do</h2>
              <ul className="team-key-points">
                {teamInfo.keyPoints.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>

            <div className={`team-details-card ${isVisible ? 'animate-slide-right' : ''}`}>
              <h2>Skills & Focus Areas</h2>
              <div className="skills-grid">
                {teamInfo.skills.map((skill, index) => (
                  <div key={index} className="skill-tag">
                    {skill}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Research Publications Section */}
          <div className={`research-section ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2 className="research-section-title">Research Publications</h2>
            <p className="research-section-subtitle">Our latest research articles and publications</p>
            <div className="research-placeholder-grid">
              <div className="research-placeholder-card">
                <div className="placeholder-icon">📄</div>
                <h3>Research Article 1</h3>
                <p className="placeholder-text">[Upload research article here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="research-placeholder-card">
                <div className="placeholder-icon">📄</div>
                <h3>Research Article 2</h3>
                <p className="placeholder-text">[Upload research article here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="research-placeholder-card">
                <div className="placeholder-icon">📄</div>
                <h3>Research Article 3</h3>
                <p className="placeholder-text">[Upload research article here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
            </div>
          </div>

          {/* Newsletter Issues Section */}
          <div className={`research-section ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2 className="research-section-title">Newsletter Issues</h2>
            <p className="research-section-subtitle">Monthly newsletters featuring interviews, insights, and stories</p>
            <div className="newsletter-cta-section">
              <p>Browse our complete newsletter archive with searchable content, organized by date and topic.</p>
              <Link to="/newsletter-archive" className="btn btn-primary">
                View Newsletter Archive
              </Link>
            </div>
          </div>

          {/* Research Findings Section */}
          <div className={`research-section ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2 className="research-section-title">Research Findings</h2>
            <p className="research-section-subtitle">Key insights and findings from our research</p>
            <div className="findings-placeholder-container">
              <div className="finding-placeholder-item">
                <div className="placeholder-icon">🔍</div>
                <div className="finding-content">
                  <h3>Research Finding 1</h3>
                  <p className="placeholder-text">[Add research finding description here]</p>
                </div>
              </div>
              <div className="finding-placeholder-item">
                <div className="placeholder-icon">🔍</div>
                <div className="finding-content">
                  <h3>Research Finding 2</h3>
                  <p className="placeholder-text">[Add research finding description here]</p>
                </div>
              </div>
              <div className="finding-placeholder-item">
                <div className="placeholder-icon">🔍</div>
                <div className="finding-content">
                  <h3>Research Finding 3</h3>
                  <p className="placeholder-text">[Add research finding description here]</p>
                </div>
              </div>
            </div>
          </div>

          {/* Interviews & Stories Section */}
          <div className={`research-section ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2 className="research-section-title">Interviews & Stories</h2>
            <p className="research-section-subtitle">Stories and interviews highlighting the experiences of older adults</p>
            <div className="stories-placeholder-grid">
              <div className="story-placeholder-card">
                <div className="placeholder-icon">🎤</div>
                <h3>Interview Title</h3>
                <p className="placeholder-text">[Upload interview content here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="story-placeholder-card">
                <div className="placeholder-icon">📖</div>
                <h3>Story Title</h3>
                <p className="placeholder-text">[Upload story content here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="story-placeholder-card">
                <div className="placeholder-icon">🎤</div>
                <h3>Interview Title</h3>
                <p className="placeholder-text">[Upload interview content here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
            </div>
          </div>

          <div className={`team-cta ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2>Interested in Joining?</h2>
            <p>We're looking for researchers, writers, and advocates who want to amplify the voices of older adults.</p>
            <div className="team-cta-buttons">
              <Link to="/get-involved" className="btn btn-primary">
                Get Involved
              </Link>
              <Link to="/teams" className="btn btn-secondary">
                View All Teams
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchTeamPage;

